import React, { useState, useRef, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Plus, Briefcase, Settings, Search, Loader2, MapPin, ChevronRight, Package, X, Bell } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { useUnreadNotifications } from '@/hooks/useUnreadNotifications';
import { cn } from '@/lib/utils';
import engineTurbineBase from '@/assets/engine-turbine-base.png';
import helicopterIcon from '@/assets/helicopter-silhouette-icon.png';
import partsToolsIcon from '@/assets/parts-tools-icon.png';
import { Button } from '@/components/ui/button';
import { useAuth } from '@/components/AuthProvider';
import chopperSwapLogo from '@/assets/chopperswap-logo-no-bg.png';
import { useNavigationSearch, SearchResult } from '@/hooks/useNavigationSearch';
import { useRecentSearches } from '@/hooks/useRecentSearches';
import { useSearchAutocomplete } from '@/hooks/useSearchAutocomplete';
import SearchSuggestions from '@/components/SearchSuggestions';
import HelicopterSilhouetteIcon from '@/components/icons/HelicopterSilhouetteIcon';

type AssetType = 'helicopter' | 'engine' | 'parts' | 'jobs' | 'services';

interface MobileTopNavProps {
  assetType: AssetType;
  onAssetTypeChange: (type: AssetType) => void;
  showSearch?: boolean;
}

const MobileTopNav = ({ assetType, onAssetTypeChange, showSearch = false }: MobileTopNavProps) => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = React.useState('');
  const [isFocused, setIsFocused] = React.useState(false);
  const [isStandalone, setIsStandalone] = React.useState(false);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const searchContainerRef = useRef<HTMLDivElement>(null);
  const { unreadCount: notificationsUnread } = useUnreadNotifications();

  const { results, isLoading: searchLoading, hasResults } = useNavigationSearch(searchQuery, 200);
  const { recentSearches, addSearch, clearSearches } = useRecentSearches();
  const { suggestions: autocompleteSuggestions } = useSearchAutocomplete(searchQuery);

  const closeDropdown = () => {
    setIsFocused(false);
  };

  const submitSearch = (e: React.FormEvent) => {
    e.preventDefault();
    const q = searchQuery.trim();
    if (!q) return;
    addSearch(q);
    closeDropdown();
    navigate(`/marketplace?search=${encodeURIComponent(q)}&asset=${assetType}&view=list`);
  };

  const handleResultClick = (result: SearchResult) => {
    addSearch(searchQuery);
    const assetParam =
      result.assetType === 'engine' ? `&engine=${result.id}` :
      result.assetType === 'parts' ? `&parts=${result.id}` :
      result.assetType === 'jobs' ? `&job=${result.id}` :
      result.assetType === 'services' ? `&service=${result.id}` : '';
    closeDropdown();
    setSearchQuery('');
    navigate(`/marketplace?asset=${result.assetType}&listing=${result.id}${assetParam}&view=map`);
  };

  const handleSuggestionClick = (term: string) => {
    setSearchQuery(term);
    searchInputRef.current?.focus();
  };

  // Close on outside click
  useEffect(() => {
    if (!isFocused) return;
    const handleClickOutside = (event: MouseEvent | TouchEvent) => {
      if (searchContainerRef.current && !searchContainerRef.current.contains(event.target as Node)) {
        setIsFocused(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('touchstart', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside);
    };
  }, [isFocused]);

  // Close on ESC
  useEffect(() => {
    if (!isFocused) return;
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setIsFocused(false);
    };
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [isFocused]);

  React.useEffect(() => {
    const mq = window.matchMedia('(display-mode: standalone)');
    setIsStandalone(mq.matches);
    const handler = (e: MediaQueryListEvent) => setIsStandalone(e.matches);
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, []);


  // When the nav has a search row, expand the global --nav-height-mobile so
  // hero sections that pad themselves by this var clear both nav + search.
  React.useEffect(() => {
    const isMobile = window.matchMedia('(max-width: 1023px)');
    const apply = () => {
      if (isMobile.matches) {
        // 56px tabs row + 36px search row + 6px bottom padding, plus any device safe area.
        document.documentElement.style.setProperty(
          '--nav-height-mobile',
          showSearch ? 'calc(6.125rem + env(safe-area-inset-top, 0px))' : 'calc(3.5rem + env(safe-area-inset-top, 0px))'
        );
      } else {
        document.documentElement.style.removeProperty('--nav-height-mobile');
      }
    };
    apply();
    isMobile.addEventListener('change', apply);
    return () => {
      isMobile.removeEventListener('change', apply);
      document.documentElement.style.removeProperty('--nav-height-mobile');
    };
  }, [showSearch]);
  
  const tabs = [
    { id: 'helicopter' as AssetType, label: 'Helicopters', icon: null, isImage: true, imgSrc: helicopterIcon },
    { id: 'engine' as AssetType, label: 'Engines', icon: null, isImage: true, imgSrc: engineTurbineBase },
    { id: 'parts' as AssetType, label: 'Parts', icon: null, isImage: true, imgSrc: partsToolsIcon },
    { id: 'jobs' as AssetType, label: 'Jobs', icon: Briefcase, isImage: false, imgSrc: null },
    { id: 'services' as AssetType, label: 'Services', icon: Settings, isImage: false, imgSrc: null },
  ];

  return (
    <nav className={cn(
      "lg:hidden fixed top-0 left-0 right-0 z-50",
      "bg-background/80 backdrop-blur-xl",
      "border-b border-border/30",
      "safe-area-top",
      "shadow-lg shadow-black/5"
    )}>
      {/* Single row: Logo + Tabs + Bell */}
      <div className="flex items-center justify-between h-14 px-3 gap-2">
        {/* Logo */}
        <Link to="/" className="flex items-center flex-shrink-0">
          <img 
            src={chopperSwapLogo} 
            alt="ChopperSwap" 
            className="w-10 h-10 object-contain dark:brightness-0 dark:invert"
          />
        </Link>
        
        {/* Asset Type Tabs - Icon-only with active label */}
        <div className={cn(
          "flex items-center bg-muted/50 backdrop-blur-xl rounded-full p-1 gap-1"
        )}>
          {tabs.map((tab) => {
            const Icon = tab.icon;
            const isActive = assetType === tab.id;
            
            return (
              <button
                key={tab.id}
                onClick={() => onAssetTypeChange(tab.id)}
                className={cn(
                  "flex items-center justify-center rounded-full",
                  "transition-all duration-200 ease-out",
                  "active:scale-95",
                  "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
                  isActive 
                    ? "h-9 px-3 gap-1.5 bg-primary text-primary-foreground font-semibold" 
                    : "w-9 h-9 sm:w-auto sm:h-9 sm:px-2.5 sm:gap-1.5 text-muted-foreground hover:bg-muted hover:text-foreground"
                )}
              >
                {tab.isImage && tab.imgSrc ? (
                  <img 
                    src={tab.imgSrc} 
                    alt={tab.label} 
                    className={cn(
                      "object-contain shrink-0",
                      tab.id === 'helicopter' ? "w-7 h-7 scale-[1.6]" : 
                      tab.id === 'parts' ? "w-7 h-7 scale-[1.3]" : "w-6 h-6",
                      "sm:w-5 sm:h-5 sm:scale-100",
                      isActive 
                        ? "brightness-0 invert" 
                        : "brightness-0 dark:invert opacity-60"
                    )}
                  />
                ) : (
                  Icon && <Icon className="w-4 h-4 shrink-0" />
                )}
                {/* Show label on tablet (sm:) always, on mobile only when active */}
                <span className={cn(
                  "text-xs font-medium whitespace-nowrap",
                  isActive ? "inline" : "hidden sm:inline"
                )}>
                  {tab.label}
                </span>
              </button>
            );
          })}
        </div>

        {/* Notifications bell */}
        {user && (
          <button
            type="button"
            onClick={() => navigate('/messages?tab=notifications')}
            className="relative flex items-center justify-center w-9 h-9 rounded-full text-muted-foreground hover:bg-muted/60 hover:text-foreground transition-colors flex-shrink-0"
            aria-label="Notifications"
          >
            <Bell className="w-5 h-5" />
            {notificationsUnread > 0 && (
              <Badge
                variant="destructive"
                className="absolute -top-0.5 -right-0.5 h-4 min-w-4 px-1 text-[10px] flex items-center justify-center"
              >
                {notificationsUnread > 9 ? '9+' : notificationsUnread}
              </Badge>
            )}
          </button>
        )}
      </div>

      {showSearch && (
        <div className="px-3 pb-1.5 pt-0">
          <div ref={searchContainerRef} className="relative mx-auto max-w-xl">
            <form onSubmit={submitSearch}>
              <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <input
                ref={searchInputRef}
                type="search"
                inputMode="search"
                value={searchQuery}
                onChange={(e) => {
                  setSearchQuery(e.target.value);
                  if (!isFocused) setIsFocused(true);
                }}
                onFocus={() => setIsFocused(true)}
                placeholder={`Search ${assetType === 'helicopter' ? 'helicopters' : assetType}…`}
                className="h-9 w-full rounded-full border border-border/60 bg-muted/40 pl-9 pr-9 text-sm placeholder:text-muted-foreground/70 focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20"
                aria-label="Search marketplace"
              />
              {searchLoading && searchQuery.length >= 2 && (
                <Loader2 className="pointer-events-none absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 animate-spin text-muted-foreground" />
              )}
              {searchQuery && !searchLoading && (
                <button
                  type="button"
                  onClick={(e) => { e.stopPropagation(); setSearchQuery(''); searchInputRef.current?.focus(); }}
                  aria-label="Clear search"
                  className="absolute right-2 top-1/2 -translate-y-1/2 p-1 rounded-full text-muted-foreground hover:text-foreground hover:bg-muted/60"
                >
                  <X className="h-3.5 h-3.5" />
                </button>
              )}
            </form>

            {isFocused && (
              <div className={cn(
                "absolute top-full left-0 right-0 mt-2 z-50",
                "bg-popover/95 backdrop-blur-xl",
                "border border-border/60 rounded-2xl",
                "shadow-2xl shadow-black/20",
                "overflow-hidden",
                "animate-in fade-in slide-in-from-top-2 duration-200"
              )}>
                {searchQuery.length < 2 ? (
                  <SearchSuggestions
                    onSuggestionClick={handleSuggestionClick}
                    recentSearches={recentSearches}
                    onClearRecent={clearSearches}
                    autocompleteSuggestions={autocompleteSuggestions}
                    query={searchQuery}
                  />
                ) : searchLoading && !hasResults ? (
                  <div className="p-4 flex items-center justify-center gap-2">
                    <Loader2 className="w-4 h-4 animate-spin text-muted-foreground" />
                    <span className="text-sm text-muted-foreground">Searching…</span>
                  </div>
                ) : hasResults ? (
                  <div className="max-h-[60vh] overflow-y-auto py-1">
                    {([
                      { key: 'helicopters', label: 'Helicopters', type: 'helicopter' as const, items: results.helicopters, icon: <HelicopterSilhouetteIcon className="w-3.5 h-3.5" tone="nav" /> },
                      { key: 'engines', label: 'Engines', type: 'engine' as const, items: results.engines, icon: <img src={engineTurbineBase} alt="" className="w-3.5 h-3.5 object-contain brightness-0 dark:invert" /> },
                      { key: 'parts', label: 'Parts & Tools', type: 'parts' as const, items: results.parts, icon: <Package className="w-3.5 h-3.5 text-primary" /> },
                      { key: 'jobs', label: 'Jobs', type: 'jobs' as const, items: results.jobs, icon: <Briefcase className="w-3.5 h-3.5 text-primary" /> },
                      { key: 'services', label: 'Services', type: 'services' as const, items: results.services, icon: <Settings className="w-3.5 h-3.5 text-primary" /> },
                    ]).filter(g => g.items.length > 0).map((group) => (
                      <div key={group.key} className="border-b border-border/40 last:border-b-0">
                        <div className="flex items-center justify-between px-3 py-1.5 bg-muted/40">
                          <div className="flex items-center gap-1.5">
                            {group.icon}
                            <span className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                              {group.label} ({group.items.length >= 5 ? '5+' : group.items.length})
                            </span>
                          </div>
                          <button
                            type="button"
                            onClick={() => {
                              addSearch(searchQuery);
                              closeDropdown();
                              navigate(`/marketplace?search=${encodeURIComponent(searchQuery)}&asset=${group.type}&view=list`);
                            }}
                            className="text-[11px] font-medium text-primary hover:text-primary/80"
                          >
                            View all
                          </button>
                        </div>
                        <div className="py-0.5">
                          {group.items.map((r) => (
                            <button
                              key={r.id}
                              type="button"
                              onClick={() => handleResultClick({ ...r, assetType: group.type })}
                              className="w-full flex items-center gap-2.5 px-3 py-2 hover:bg-muted/60 active:bg-muted/80 text-left transition-colors"
                            >
                              <div className="w-9 h-9 rounded-md bg-muted/60 overflow-hidden flex items-center justify-center flex-shrink-0">
                                {r.thumbnailUrl ? (
                                  <img
                                    src={r.thumbnailUrl}
                                    alt=""
                                    className="w-full h-full object-cover"
                                    onError={(e) => { (e.currentTarget as HTMLImageElement).style.display = 'none'; }}
                                  />
                                ) : (
                                  group.icon
                                )}
                              </div>
                              <div className="flex-1 min-w-0">
                                <div className="text-sm font-medium text-foreground truncate">{r.title}</div>
                                <div className="text-xs text-muted-foreground truncate flex items-center gap-1">
                                  {r.subtitle}
                                  {r.location && (
                                    <span className="inline-flex items-center gap-0.5">
                                      <MapPin className="w-3 h-3" />
                                      {r.location}
                                    </span>
                                  )}
                                </div>
                              </div>
                              <ChevronRight className="w-4 h-4 text-muted-foreground flex-shrink-0" />
                            </button>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="p-3">
                    <p className="text-center text-sm text-muted-foreground mb-2">
                      No results for "{searchQuery}"
                    </p>
                    {autocompleteSuggestions.length > 0 ? (
                      <SearchSuggestions
                        onSuggestionClick={handleSuggestionClick}
                        autocompleteSuggestions={autocompleteSuggestions}
                        query={searchQuery}
                      />
                    ) : (
                      <SearchSuggestions onSuggestionClick={handleSuggestionClick} />
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      )}
    </nav>
  );
};


export default MobileTopNav;
